export type ObjectDiffItem = {
  key: string
  oldValue: unknown
  newValue: unknown
}

type PlainObject = Record<string, unknown>

function getObjectKeys(obj: PlainObject): string[] {
  return Object.keys(obj)
}

function isPlainObject(val: unknown): val is PlainObject {
  return typeof val === 'object' && val !== null && !Array.isArray(val)
}

function compareObjects(
  objA: PlainObject,
  objB: PlainObject,
  returnDiff: boolean,
  excludeKeySet: Set<string>,
  pathPrefix = ''
): boolean | ObjectDiffItem[] {
  const diffList: ObjectDiffItem[] = []

  function buildKey(key: string): string {
    return pathPrefix ? `${pathPrefix}.${key}` : key
  }

  function isExcludedKey(key: string): boolean {
    return excludeKeySet.has(key)
  }

  function appendDiff(
    key: string,
    oldValue: unknown,
    newValue: unknown
  ): false {
    if (returnDiff) {
      diffList.push({ key, oldValue, newValue })
      return false
    }
    return false
  }

  function compareNested(
    valA: unknown,
    valB: unknown,
    key: string
  ): boolean | ObjectDiffItem[] {
    const fullKey = buildKey(key)

    if (valA === valB) return true

    if (Array.isArray(valA) && Array.isArray(valB)) {
      if (valA.length !== valB.length) {
        return appendDiff(fullKey, valA, valB)
      }

      for (let i = 0; i < valA.length; i++) {
        const itemKey = `${fullKey}[${i}]`
        const itemA = valA[i]
        const itemB = valB[i]

        if (isPlainObject(itemA) && isPlainObject(itemB)) {
          const nestedResult = compareObjects(
            itemA,
            itemB,
            returnDiff,
            excludeKeySet,
            itemKey
          )
          if (returnDiff) {
            diffList.push(...(nestedResult as ObjectDiffItem[]))
          } else if (!nestedResult) {
            return false
          }
        } else if (itemA !== itemB) {
          if (returnDiff) {
            diffList.push({ key: itemKey, oldValue: itemA, newValue: itemB })
          } else {
            return false
          }
        }
      }
      return true
    }

    if (isPlainObject(valA) && isPlainObject(valB)) {
      const nestedResult = compareObjects(
        valA,
        valB,
        returnDiff,
        excludeKeySet,
        fullKey
      )
      if (returnDiff) {
        diffList.push(...(nestedResult as ObjectDiffItem[]))
      } else {
        return nestedResult as boolean
      }
      return true
    }

    return appendDiff(fullKey, valA, valB)
  }

  const keysA = getObjectKeys(objA)
  const keysB = getObjectKeys(objB)
  const allKeys = new Set([...keysA, ...keysB])

  for (const key of allKeys) {
    if (isExcludedKey(key)) continue

    const hasA = Object.prototype.hasOwnProperty.call(objA, key)
    const hasB = Object.prototype.hasOwnProperty.call(objB, key)

    if (!hasA || !hasB) {
      const result = appendDiff(
        buildKey(key),
        hasA ? objA[key] : undefined,
        hasB ? objB[key] : undefined
      )
      if (!returnDiff) return result
      continue
    }

    const valA = objA[key]
    const valB = objB[key]

    if (
      isPlainObject(valA) ||
      Array.isArray(valA) ||
      isPlainObject(valB) ||
      Array.isArray(valB)
    ) {
      const nestedResult = compareNested(valA, valB, key)
      if (!returnDiff && nestedResult === false) return false
    } else if (valA !== valB) {
      const result = appendDiff(buildKey(key), valA, valB)
      if (!returnDiff) return result
    }
  }

  return returnDiff ? diffList : true
}

export function isObjectEqual(
  objA: PlainObject,
  objB: PlainObject,
  excludeKeys?: string[],
  returnDiff?: false
): boolean

export function isObjectEqual(
  objA: PlainObject,
  objB: PlainObject,
  excludeKeys: string[] | undefined,
  returnDiff: true
): ObjectDiffItem[]

export function isObjectEqual(
  objA: PlainObject,
  objB: PlainObject,
  excludeKeys: string[] = [],
  returnDiff = false
): boolean | ObjectDiffItem[] {
  const excludeKeySet = new Set(excludeKeys)

  if (objA === objB) {
    return returnDiff ? [] : true
  }

  if (!objA || !objB) {
    if (returnDiff) {
      return [{ key: '', oldValue: objA, newValue: objB }]
    }
    return false
  }

  return compareObjects(objA, objB, returnDiff, excludeKeySet)
}
